<template>
  <div class="slider-input">
    <label class="slider-input__label" :for="inputId">{{ label }}</label>
    <div class="slider-input__row">
      <input
        :id="inputId"
        type="range"
        class="slider-input__range"
        :value="unwrappedValue"
        :min="min"
        :max="max"
        :step="step"
        :aria-valuemin="min"
        :aria-valuemax="max"
        :aria-valuenow="unwrappedValue"
        :aria-valuetext="`${displayValue}${unit ? ` ${unit}` : ''}`"
        role="slider"
        @input="onInput"
      />
      <span class="slider-input__value" aria-live="polite">
        {{ displayValue }}<template v-if="unit">{{ unit }}</template>
      </span>
    </div>
    <div class="slider-input__limits">
      <span>{{ min }}<template v-if="unit">{{ unit }}</template></span>
      <span>{{ max }}<template v-if="unit">{{ unit }}</template></span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

let sliderUidCounter = 0;

export default Vue.extend({
  name: "SliderInput",
  props: {
    label: { type: String, required: true },
    value: { type: [Number, Object], required: true },
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    step: { type: Number, default: 0.1 },
    unit: { type: String, default: "" },
  },
  created() {
    sliderUidCounter += 1;
    this.sliderUid = sliderUidCounter;
  },
  data() {
    return {
      sliderUid: 0,
    };
  },
  computed: {
    inputId(): string {
      return `slider-${this.sliderUid}-${this.label.replace(/\s/g, "-")}`;
    },
    /** Ref/ComputedRef が渡った場合にアンラップして数値にする */
    unwrappedValue(): number {
      const v = this.value;
      if (v != null && typeof v === "object" && "value" in v) {
        return Number((v as { value: unknown }).value);
      }
      return Number(v);
    },
    displayValue(): string {
      const v = this.unwrappedValue;
      return Number.isFinite(v) ? v.toFixed(1) : "";
    },
  },
  methods: {
    onInput(e: Event) {
      const el = e.target as HTMLInputElement;
      const num = parseFloat(el.value);
      if (Number.isFinite(num)) this.$emit("input", num);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.slider-input {
  margin-bottom: 1rem;

  &__label {
    display: block;
    font-size: 0.9em;
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
    margin-bottom: 0.35rem;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__range {
    flex: 1;
    min-width: 0;
    height: 8px;
    -webkit-appearance: none;
    appearance: none;
    background: var(--color-border);
    border-radius: 4px;
    outline: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--color-primary);
      cursor: pointer;
      border: 2px solid var(--color-background-normal);

      &:hover {
        filter: brightness(1.1);
      }
    }

    &::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: var(--color-primary);
      cursor: pointer;
      border: 2px solid var(--color-background-normal);

      &:hover {
        filter: brightness(1.1);
      }
    }
  }

  &__value {
    flex-shrink: 0;
    min-width: 3.5em;
    font-size: 0.9em;
    color: var(--color-text-low);
    text-align: right;
  }

  &__limits {
    display: flex;
    justify-content: space-between;
    margin-top: 0.2rem;
    font-size: 0.75em;
    color: var(--color-text-low);
  }
}
</style>
